// Wrapper around `gh repo create <org>/<name> --<vis> --source=<folder>
// --remote=origin --push`.
//
// stdio: pipe (KHÔNG inherit) — capture stderr để classify lỗi chính xác. gh repo
// create --source KHÔNG có interactive prompt nên không cần inherit. Trước đây
// inherit + heuristic "exit 1 = đã tồn tại" báo SAI mọi lỗi khác (auth fail,
// folder không tồn tại) thành "repo đã tồn tại" → user đi sai recovery path.
import { spawnSync } from "node:child_process";
import { existsSync, statSync } from "node:fs";
import { classifyRepoCreateError } from "./create-empty-github-repo.js";
import type { RepoVisibility } from "./validate-repo-name-and-visibility.js";

export class RepoAlreadyExistsError extends Error {
  constructor(fullName: string) {
    super(`Repo "${fullName}" đã tồn tại trên GitHub. Đổi tên hoặc xóa repo cũ.`);
    this.name = "RepoAlreadyExistsError";
  }
}

export class GhRepoCreateError extends Error {
  reason: "no-permission" | "network" | "missing-folder" | "unknown";
  constructor(reason: GhRepoCreateError["reason"], message: string) {
    super(message);
    this.name = "GhRepoCreateError";
    this.reason = reason;
  }
}

export interface ExecuteGhRepoCreateInput {
  folder: string;
  org: string;
  name: string;
  visibility: RepoVisibility;
}

export interface ExecuteGhRepoCreateOutput {
  sshUrl: string;
  httpsUrl: string;
}

export function executeGhRepoCreate(input: ExecuteGhRepoCreateInput): ExecuteGhRepoCreateOutput {
  const fullName = `${input.org}/${input.name}`;

  // Validate folder source TRƯỚC khi gọi gh — gh báo lỗi mơ hồ khi folder không
  // tồn tại; ta cho thông điệp rõ ràng + reason riêng để recovery đúng.
  if (!existsSync(input.folder) || !statSync(input.folder).isDirectory()) {
    throw new GhRepoCreateError(
      "missing-folder",
      `Folder nguồn không tồn tại: ${input.folder}. Kiểm tra lại đường dẫn.`,
    );
  }

  const args = [
    "repo",
    "create",
    fullName,
    `--${input.visibility}`,
    "--source",
    input.folder,
    "--remote",
    "origin",
    "--push",
  ];
  const r = spawnSync("gh", args, { stdio: ["ignore", "pipe", "pipe"], encoding: "utf8" });
  if (r.status !== 0) {
    const stderr = (r.stderr || "").trim();
    if (stderr) process.stderr.write(`${stderr}\n`);
    // Classify stderr thay vì đoán theo exit code → recovery đúng nguyên nhân.
    const reason = classifyRepoCreateError(stderr);
    if (reason === "name-exists") throw new RepoAlreadyExistsError(fullName);
    if (reason === "no-permission") {
      throw new GhRepoCreateError(
        "no-permission",
        `Không có quyền tạo repo ${fullName}. Kiểm tra đăng nhập đúng account/org chưa (gh auth status).`,
      );
    }
    if (reason === "network") {
      throw new GhRepoCreateError("network", `Lỗi mạng khi tạo repo ${fullName}.`);
    }
    throw new GhRepoCreateError("unknown", `gh repo create thất bại (exit ${r.status}).`);
  }
  return {
    sshUrl: `git@github.com:${fullName}.git`,
    httpsUrl: `https://github.com/${fullName}.git`,
  };
}
